import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environment/environment';
import { RuntimeConfigService } from '../services/runtime-config.service';

export interface Offer {
  _id?: string;
  offerId?: string;
  title: string;
  banner: string;
  link: string;
  orderNo: number;
  status: string;
  categoryId?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
}

export interface OfferResponse {
  offers: Offer[];
  total: number;
  categoryId?: string;
  filters_applied?: any;
  success?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OfferService {
  // Dynamic API URL - prefers runtime config, falls back to environment
  private get apiUrl(): string {
    return this.configService?.apiUrl || environment.baseUrl;
  }

  constructor(
    private http: HttpClient,
    private configService: RuntimeConfigService
  ) { }

  /**
   * Get list of active offers for user display - PUBLIC endpoint, no auth required
   * Uses /offer/front endpoint (same pattern as /category/front)
   * @param categoryId Optional category ID to filter offers
   */
  getActiveOffers(categoryId?: string): Observable<OfferResponse> {
    let url = `${this.apiUrl}/offer/front`;
    
    // Add categoryId as query parameter if provided
    if (categoryId) {
      url += `?categoryId=${encodeURIComponent(categoryId)}`;
    }
    
    // Public endpoint - NO auth headers at all
    return this.http.get<OfferResponse>(url)
      .pipe(
        map(response => {
          // Sort by orderNo (already sorted by backend, but just in case)
          if (response && response.offers) {
            response.offers.sort((a, b) => a.orderNo - b.orderNo);
          }
          return response;
        }),
        catchError(error => {
          console.error('Error loading offers:', error);
          return of({
            offers: [],
            total: 0,
            success: false
          });
        })
      );
  }

  /**
   * Get offers filtered by category - convenience method
   */
  getOffersByCategory(categoryId: string): Observable<OfferResponse> {
    return this.getActiveOffers(categoryId);
  }

  /**
   * Handle image error with fallback
   */
  getOfferImageUrl(banner: string): string {
    if (!banner) {
      return '/assets/images/default-offer.png';
    }
    
    // If it's already a full URL, return it
    if (banner.startsWith('http://') || banner.startsWith('https://')) {
      return banner;
    }
    
    // If it's a path, construct the full URL
    return `${environment.baseUrl}${banner}`;
  }
}
